import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import {
  EventChatMessage,
  EventChatAudit,
  EventCommunicationConfig,
  EventCommunicationPreferences,
  EventCommunicationToggles,
  EventConversation,
  EventMention
} from 'Src/models/eventCommunication';
import {
  createEventConversation,
  getEventChatMessages,
  getEventCommunicationConfig,
  getEventCommunicationRecipients,
  deleteEventChatMessage,
  editEventChatMessage,
  getEventConversations,
  getMentionCandidates,
  markEventConversationRead,
  moderateEventChatMessage,
  reportEventChatMessage,
  resolveEventChatMessageReport,
  sendEventChatMessage,
  updateConversationStatus,
  updateEventCommunicationOverrides,
  updateReadReceiptPrivacy,
  updateConversationMute,
  updateEventCommunicationsMute,
  getConversationParticipants,
  updateConversationDetails,
  updateConversationLegalHold,
  getEventCommunicationPreferences,
  updateEventCommunicationPreferences,
  getConversationAudit,
  generateSafeguardingReport
} from 'Src/helpers/service/eventCommunications';
import './EventCommunications.scss';
import { AccessibleModal } from './AccessibleModal';

export interface Props {
  user: AppUser;
  eventId: string;
  subjectPermissionId?: string;
  subjects?: Array<{ permissionId: string; label: string }>;
  initialConversationId?: string;
  focusMessageId?: string;
  threadOnly?: boolean;
  assignedToMe?: boolean;
  onToggleAssignment?: () => void;
  onShowAudit?: () => void;
  onExportSafeguarding?: () => void;
  exportingSafeguarding?: boolean;
  panel?: 'CHATS' | 'SETTINGS';
}
interface State {
  config?: EventCommunicationConfig;
  conversations: EventConversation[];
  selected?: EventConversation;
  messages: EventChatMessage[];
  text: string;
  loading: boolean;
  sending: boolean;
  error: string;
  notice: string;
  editingSettings: boolean;
  replyTo?: EventChatMessage;
  candidates: EventMention[];
  mentions: EventMention[];
  selectedSubjectPermissionId?: string;
  editingMessageId?: string;
  editingText: string;
  actionMessage?: EventChatMessage;
  actionType?: 'DELETE' | 'REPORT' | 'REJECT';
  actionReason: string;
  reportConcern: string;
  activePanel: 'CHATS' | 'SETTINGS';
  mentionPickerDismissed: boolean;
  mentionActiveIndex: number;
  recipientPickerOpen: boolean;
  recipientSearch: string;
  recipientCandidates: EventMention[];
  selectedRecipients: EventMention[];
  removedRecipients: EventMention[];
  recipientRole: string;
  conversationTitle: string;
  participantsOpen: boolean;
  participantsExpanded: boolean;
  conversationParticipants: EventMention[];
  legalHoldReason: string;
  notificationPreferences?: EventCommunicationPreferences;
  savingNotificationPreferences: boolean;
  expandedReceiptMessageId?: string;
  expandedParentMessageIds: string[];
  threadMenuOpen?: boolean;
  loadingOlder: boolean;
  hasOlderMessages: boolean;
  audit?: EventChatAudit[];
  exportingSafeguarding: boolean;
}

export class EventCommunications extends React.Component<Props, State> {
  private openRequest = 0;
  private refreshTimer?: number;
  private refreshBusy = false;
  private pendingSend?: { signature: string; id: string };
  private messagesRef = React.createRef<HTMLDivElement>();
  private composerRef = React.createRef<HTMLTextAreaElement>();

  private clientMessageId = (signature: string) => {
    if (this.pendingSend && this.pendingSend.signature === signature) return this.pendingSend.id;
    const cryptoApi = window.crypto as any;
    const id = cryptoApi && typeof cryptoApi.randomUUID === 'function'
      ? cryptoApi.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.pendingSend = { signature, id };
    return id;
  };
  state: State = {
    conversations: [],
    messages: [],
    text: '',
    loading: true,
    sending: false,
    error: '',
    notice: '',
    editingSettings: false,
    candidates: [],
    mentions: [],
    editingText: '',
    actionReason: '',
    reportConcern: '',
    activePanel: 'CHATS',
    mentionPickerDismissed: false,
    mentionActiveIndex: 0,
    recipientPickerOpen: false,
    recipientSearch: '',
    recipientCandidates: [],
    selectedRecipients: [],
    removedRecipients: [],
    recipientRole: 'ALL',
    conversationTitle: '',
    participantsOpen: false,
    participantsExpanded: false,
    loadingOlder: false,
    hasOlderMessages: false,
    exportingSafeguarding: false,
    conversationParticipants: [],
    legalHoldReason: '',
    savingNotificationPreferences: false,
    expandedParentMessageIds: [],
    selectedSubjectPermissionId:
      this.props.subjectPermissionId ||
      (this.props.subjects && this.props.subjects.length === 1 ? this.props.subjects[0].permissionId : undefined)
  };

  componentDidMount() {
    this.load();
    document.addEventListener('mousedown', this.closeMentionPickerOnOutsideClick);
    document.addEventListener('visibilitychange', this.refreshWhenVisible);
    window.addEventListener('focus', this.refreshOpenConversation);
    this.refreshTimer = window.setInterval(this.refreshOpenConversation, 10000);
  }
  componentWillUnmount() {
    this.openRequest += 1;
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
    document.removeEventListener('mousedown', this.closeMentionPickerOnOutsideClick);
    document.removeEventListener('visibilitychange', this.refreshWhenVisible);
    window.removeEventListener('focus', this.refreshOpenConversation);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.focusMessageId && (prevProps.focusMessageId !== this.props.focusMessageId || prevState.messages !== this.state.messages)) {
      const target = document.getElementById(`event-message-${this.props.focusMessageId}`);
      if (target) target.scrollIntoView({block: 'center', behavior: prevProps.focusMessageId === this.props.focusMessageId ? 'auto' : 'smooth'});
    }
  }

  private refreshWhenVisible = () => {
    if (!document.hidden) this.refreshOpenConversation();
  };

  private refreshOpenConversation = async () => {
    const {selected, messages} = this.state;
    const conversationId = selected && this.id(selected);
    if (!conversationId || document.hidden || this.refreshBusy) return;
    this.refreshBusy = true;
    const request = this.openRequest;
    const lastUpdated = messages.reduce((latest, message) => {
      const value = message.updatedAt || message.createdAt;
      return !latest || new Date(value).getTime() > new Date(latest).getTime() ? value : latest;
    }, '' as string);
    try {
      const [changed, conversations] = await Promise.all([
        getEventChatMessages(this.props.user, this.props.eventId, conversationId, 0, 200, lastUpdated || undefined),
        getEventConversations(this.props.user, this.props.eventId)
      ]);
      if (request !== this.openRequest) return;
      this.setState(state => {
        const changedById = new Map(changed.map(message => [this.id(message), message]));
        const existingIds = new Set(state.messages.map(message => this.id(message)));
        const merged = state.messages.map(message => changedById.get(this.id(message)) || message);
        changed.slice().reverse().forEach(message => { if (!existingIds.has(this.id(message))) merged.push(message); });
        const refreshedSelected = conversations.find(item => this.id(item) === conversationId) || state.selected;
        return {messages: merged, conversations, selected: refreshedSelected};
      }, () => {
        if (changed.length) {
          const newest = this.state.messages[this.state.messages.length - 1];
          markEventConversationRead(this.props.user, this.props.eventId, conversationId, newest && this.id(newest)).catch(() => undefined);
        }
      });
    } catch (error) {
      // Background refresh is deliberately silent; the current thread remains usable.
    } finally {
      this.refreshBusy = false;
    }
  };

  private closeMentionPickerOnOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (this.mentionQuery() !== undefined && (!target || !target.closest('.eventComms__composer'))) {
      this.setState({ mentionPickerDismissed: true });
    }
    if (this.state.threadMenuOpen && (!target || !target.closest('.eventComms__threadTools'))) this.setState({threadMenuOpen: false});
  };

  private id = (item: { id?: string; _id?: string }) => item.id || item._id;
  private sortConversations = (items: EventConversation[]) =>
    items
      .map((item, index) => ({ item, index }))
      .sort((left, right) => {
        const leftIsPublic = String(left.item.type).toUpperCase() === 'PUBLIC';
        const rightIsPublic = String(right.item.type).toUpperCase() === 'PUBLIC';
        if (leftIsPublic === rightIsPublic) return left.index - right.index;
        return leftIsPublic ? -1 : 1;
      })
      .map(entry => entry.item);
  private dayLabel = (value: string) => {
    const date = new Date(value); const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    if (day === start) return 'Today';
    if (day === start - 86400000) return 'Yesterday';
    if (day > start - 7 * 86400000) return date.toLocaleDateString([], {weekday: 'long'});
    return date.toLocaleDateString([], {day: 'numeric', month: 'short'});
  };
  private conversationLabel = (conversation: EventConversation) => {
    if (conversation.type === 'PUBLIC') return 'Event chat';
    if (conversation.title) return conversation.title;
    const summary = conversation.participantsSummary;
    if (!summary || !summary.participants.length) return 'Private chat';
    const names = summary.participants.map(person => person.name).join(', ');
    return `${names}${summary.totalCount > summary.participants.length ? ` + ${summary.totalCount - summary.participants.length}` : ''}`;
  };
  private conversationInitiatorLabel = (conversation: EventConversation) => {
    if (conversation.createdByRole === 'PARENT') return 'Parent initiated';
    if (conversation.createdByRole === 'STUDENT') return 'Student initiated';
    return 'Staff initiated';
  };
  private composerStatus = (conversation: EventConversation, config: EventCommunicationConfig) => {
    if (conversation.status === 'ARCHIVED') return 'This conversation is archived. Its history remains available for safeguarding.';
    if (conversation.status === 'CLOSED') return 'This conversation is resolved. No new messages can be sent.';
    if (conversation.status === 'READ_ONLY') {
      return config.canModerate
        ? 'Staff announcements only. Participants can read, but only staff can post.'
        : 'Staff announcements only. You can read messages, but only staff can post.';
    }
    if (conversation.type === 'PRIVATE') return 'Visible only to chat participants and authorised event staff.';
    if (!config.canModerate) return config.postingPolicy === 'MODERATED' ? 'Staff approve your messages before others can see them.' : 'Everyone on this event can see messages in this chat.';
    const resolved = (key: 'publicChatParentsPosting' | 'publicChatStudentsPosting') => (config.eventOverrides && config.eventOverrides[key]) || (config.schoolDefaults && config.schoolDefaults[key]) || 'IMMEDIATE';
    const label = value => value === 'MODERATED' ? 'approved by staff' : value === 'DISABLED' ? 'read only' : 'post immediately';
    return `Parents’ messages are ${label(resolved('publicChatParentsPosting'))}. Students’ messages are ${label(resolved('publicChatStudentsPosting'))}.`;
  };

  private load = async () => {
    try {
      const config = await getEventCommunicationConfig(this.props.user, this.props.eventId);
      if (config.available === false) {
        this.setState({config, conversations: [], loading: false});
        return;
      }
      const isStaff = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'].includes(this.props.user.activeRole);
      const hasChannelAccess = config.eventEnabled !== false && (config.publicChatEnabled || config.privateMessagesAccessible || config.privateMessagesEnabled);
      const canReviewRetainedHistory = Boolean(this.props.threadOnly && isStaff);
      if (!hasChannelAccess && !canReviewRetainedHistory) {
        const notificationPreferences = await getEventCommunicationPreferences(this.props.user);
        this.setState({config, conversations: [], notificationPreferences, loading: false});
        return;
      }
      const [loadedConversations, notificationPreferences] = await Promise.all([
        getEventConversations(this.props.user, this.props.eventId),
        getEventCommunicationPreferences(this.props.user)
      ]);
      let conversations = loadedConversations;
      if (config.eventEnabled !== false && config.publicChatEnabled && !conversations.some(item => String(item.type).toUpperCase() === 'PUBLIC')) {
        const publicConversation = await createEventConversation(this.props.user, this.props.eventId, 'PUBLIC');
        conversations = [publicConversation, ...conversations];
      }
      this.setState({ config, conversations, notificationPreferences, loading: false }, () => {
        const initial = conversations.find(item => this.id(item) === this.props.initialConversationId);
        const defaultConversation = initial || conversations.find(item => item.type === 'PUBLIC') || conversations[0];
        if (defaultConversation) this.open(defaultConversation);
      });
    } catch (error) {
      this.setState({ loading: false, error: 'Chats could not be loaded.' });
    }
  };

  private open = async (conversation: EventConversation) => {
    const conversationId = this.id(conversation);
    if (!conversationId) return;
    const request = ++this.openRequest;
    try {
      const [messages, candidates] = await Promise.all([
        getEventChatMessages(this.props.user, this.props.eventId, conversationId, 0, 200, undefined, this.props.focusMessageId),
        getMentionCandidates(this.props.user, this.props.eventId, conversationId)
      ]);
      if (request !== this.openRequest) return;
      const latestMessageId = messages.length ? this.id(messages[0]) : undefined;
      this.setState({
        selected: conversation,
        messages: messages.slice().reverse(),
        candidates,
        hasOlderMessages: messages.length === 200,
        loadingOlder: false,
        mentions: [],
        replyTo: undefined,
        error: ''
      }, () => { const node = this.messagesRef.current; if (node) node.scrollTop = node.scrollHeight; });
      markEventConversationRead(this.props.user, this.props.eventId, conversationId, latestMessageId)
        .then(() => window.dispatchEvent(new CustomEvent('sit:event-communications-unread-changed', { detail: { eventId: this.props.eventId } })))
        .catch(() => undefined);
    } catch (error) {
      this.setState({ error: 'Messages could not be loaded.' });
    }
  };

  private loadOlderMessages = async () => {
    const {selected, messages, loadingOlder} = this.state;
    const conversationId = selected && this.id(selected);
    if (!conversationId || loadingOlder) return;
    const request = this.openRequest;
    this.setState({loadingOlder: true});
    try {
      const older = await getEventChatMessages(this.props.user, this.props.eventId, conversationId, messages.length);
      if (request !== this.openRequest) return;
      this.setState(state => ({
        messages: [...older.slice().reverse(), ...state.messages],
        hasOlderMessages: older.length === 200,
        loadingOlder: false
      }));
    } catch (error) {
      if (request === this.openRequest) this.setState({loadingOlder: false, error: 'Older messages could not be loaded.'});
    }
  };

  private start = async (type: 'PUBLIC' | 'PRIVATE') => {
    try {
      const conversation = await createEventConversation(
        this.props.user,
        this.props.eventId,
        type,
        this.state.selectedSubjectPermissionId,
        undefined,
        type === 'PRIVATE' ? this.state.conversationTitle.trim() : undefined
      );
      const existingConversation = this.state.conversations.find(item => this.id(item) === this.id(conversation));
      const conversations = existingConversation
        ? this.state.conversations
        : [conversation, ...this.state.conversations];
      const subjectLabel = (this.props.subjects || []).find(
        subject => subject.permissionId === this.state.selectedSubjectPermissionId
      )?.label;
      this.setState({
        conversations,
        conversationTitle: '',
        notice: existingConversation
          ? `A private chat${subjectLabel ? ` about ${subjectLabel}` : ''} already exists. We opened the existing conversation.`
          : ''
      });
      await this.open(conversation);
    } catch (error) {
      this.setState({
        error: 'The conversation could not be started. If several children are attending, select the child first.'
      });
    }
  };

  private groupRecipients = (items: EventMention[]) =>
    Array.from(
      items
        .reduce((map, item) => {
          const key = `${item.userId}|${item.role}`;
          const existing = map.get(key);
          const children = [item.relatedStudentLabel].filter(Boolean) as string[];
          if (existing) {
            existing.relatedStudentLabels = Array.from(
              new Set([...(existing.relatedStudentLabels || []), ...children])
            );
          } else map.set(key, { ...item, relatedStudentLabels: children });
          return map;
        }, new Map<string, EventMention>())
        .values()
    );
  private openRecipientPicker = async () => {
    try {
      const recipientCandidates = this.groupRecipients(
        await getEventCommunicationRecipients(this.props.user, this.props.eventId)
      );
      this.setState({
        recipientPickerOpen: true,
        recipientCandidates,
        selectedRecipients: [],
        removedRecipients: [],
        recipientSearch: '',
        recipientRole: 'ALL',
        conversationTitle: ''
      });
    } catch (error) {
      this.setState({ error: 'Participants could not be loaded.' });
    }
  };
  private toggleRecipient = (candidate: EventMention) =>
    this.setState(state => ({
      selectedRecipients: state.selectedRecipients.some(
        item => item.userId === candidate.userId && item.permissionId === candidate.permissionId
      )
        ? state.selectedRecipients.filter(
            item => !(item.userId === candidate.userId && item.permissionId === candidate.permissionId)
          )
        : [...state.selectedRecipients, candidate]
    }));
  private createStaffPrivateConversation = async () => {
    try {
      const conversation = await createEventConversation(
        this.props.user,
        this.props.eventId,
        'PRIVATE',
        undefined,
        this.state.selectedRecipients,
        this.state.conversationTitle.trim()
      );
      this.setState(state => ({ recipientPickerOpen: false, conversations: [conversation, ...state.conversations] }));
      await this.open(conversation);
    } catch (error) {
      this.setState({ error: 'The private conversation could not be created.' });
    }
  };
  private filteredRecipients = () =>
    this.state.recipientCandidates.filter(
      item =>
        (this.state.recipientRole === 'ALL' || item.role === this.state.recipientRole || (this.state.recipientRole === 'STAFF' && ['ADMIN','MANAGER','TEACHER','COACH'].includes(item.role || ''))) &&
        `${item.label} ${(item.relatedStudentLabels || []).join(' ')}`
          .toLowerCase()
          .includes(this.state.recipientSearch.toLowerCase())
    );
  private selectAllFiltered = () =>
    this.setState(state => ({
      selectedRecipients: Array.from(
        new Map(
          [...state.selectedRecipients, ...this.filteredRecipients()].map(item => [`${item.userId}|${item.role}`, item])
        ).values()
      )
    }));
  private selectRecipients = (recipients: EventMention[]) => this.setState(state => ({selectedRecipients: Array.from(new Map([...state.selectedRecipients, ...recipients].map(item => [`${item.userId}|${item.permissionId || ''}|${item.role}`, item])).values())}));
  private recipientGroups = () => {
    const recipients = this.filteredRecipients();
    return [
      {key: 'PARENT', label: 'Parents on this event', items: recipients.filter(item => item.role === 'PARENT')},
      {key: 'STUDENT', label: 'Students on this event', items: recipients.filter(item => item.role === 'STUDENT')},
      {key: 'STAFF', label: 'Staff on this event', items: recipients.filter(item => ['ADMIN','MANAGER','TEACHER','COACH'].includes(item.role || ''))}
    ].filter(group => group.items.length);
  };
  private openParticipants = async () => {
    const selected = this.state.selected;
    if (!selected) return;
    try {
      const [conversationParticipants, recipients] = await Promise.all([
        getConversationParticipants(this.props.user, this.props.eventId, this.id(selected)),
        this.state.config && this.state.config.canModerate
          ? getEventCommunicationRecipients(this.props.user, this.props.eventId)
          : Promise.resolve([])
      ]);
      this.setState({
        participantsOpen: true,
        participantsExpanded: false,
        conversationParticipants,
        recipientCandidates: this.groupRecipients(recipients),
        selectedRecipients: [],
        recipientSearch: '',
        recipientRole: 'ALL',
        conversationTitle: selected.title || '',
        legalHoldReason: selected.legalHoldReason || ''
      });
    } catch (error) {
      this.setState({ error: 'Conversation participants could not be loaded.' });
    }
  };

  private setLegalHold = async (legalHold: boolean) => {
    const selected = this.state.selected;
    if (!selected || (legalHold && !this.state.legalHoldReason.trim())) return;
    try {
      const updated = await updateConversationLegalHold(
        this.props.user,
        this.props.eventId,
        this.id(selected),
        legalHold,
        this.state.legalHoldReason.trim()
      );
      this.setState(state => ({
        selected: {...selected, ...updated},
        conversations: state.conversations.map(item => this.id(item) === this.id(selected) ? {...item, ...updated} : item),
        legalHoldReason: updated.legalHoldReason || ''
      }));
    } catch (error) {
      this.setState({error: 'The legal hold setting could not be updated.'});
    }
  };
  private saveConversationDetails = async () => {
    const { selected, conversationTitle, selectedRecipients, removedRecipients } = this.state;
    if (!selected) return;
    try {
      const updated = await updateConversationDetails(
        this.props.user,
        this.props.eventId,
        this.id(selected),
        conversationTitle.trim(),
        selectedRecipients,
        removedRecipients
      );
      this.setState(state => ({
        selected: { ...selected, ...updated },
        conversations: state.conversations.map(item =>
          this.id(item) === this.id(selected) ? { ...item, ...updated } : item
        ),
        participantsOpen: false,
        selectedRecipients: [],
        removedRecipients: []
      }));
    } catch (error) {
      this.setState({ error: 'Conversation details could not be saved.' });
    }
  };

  private send = async (event: React.FormEvent) => {
    event.preventDefault();
    const conversationId = this.state.selected && this.id(this.state.selected);
    const text = this.state.text.trim();
    if (!conversationId || !text) return;
    const signature = JSON.stringify({
      conversationId,
      text,
      replyTo: this.state.replyTo && this.id(this.state.replyTo),
      mentions: this.state.mentions.map(item => `${item.userId}|${item.permissionId || ''}`)
    });
    const clientMessageId = this.clientMessageId(signature);
    this.setState({ sending: true, error: '' });
    try {
      const message = await sendEventChatMessage(
        this.props.user,
        this.props.eventId,
        conversationId,
        text,
        this.state.replyTo && this.id(this.state.replyTo),
        this.state.mentions,
        clientMessageId
      );
      this.pendingSend = undefined;
      this.setState(state => ({
        messages: state.messages.some(item => this.id(item) === this.id(message))
          ? state.messages.map(item => this.id(item) === this.id(message) ? message : item)
          : [...state.messages, {
              ...message,
              receiptSummary: message.receiptSummary || { readCount: 0, privacyProtectedCount: 0, readers: [] }
            }],
        text: '',
        sending: false,
        replyTo: undefined,
        mentions: []
      }));
    } catch (error) {
      this.setState({ sending: false, error: 'The message could not be sent. Please try again.' });
    }
  };
  private composerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') this.setState({ mentionPickerDismissed: true });
    const mentionMatches = typeof this.mentionQuery() === 'string' ? this.filteredMentionCandidates() : [];
    if (mentionMatches.length && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) { event.preventDefault(); this.setState(state => ({mentionActiveIndex: (state.mentionActiveIndex + (event.key === 'ArrowDown' ? 1 : -1) + mentionMatches.length) % mentionMatches.length})); return; }
    if (mentionMatches.length && event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.addMention(mentionMatches[this.state.mentionActiveIndex] || mentionMatches[0]); return; }
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (this.state.text.trim() && !this.state.sending) this.send(event as any);
    }
  };
  private showAudit = async () => {
    if (this.props.onShowAudit) return this.props.onShowAudit();
    const selected = this.state.selected;
    if (!selected) return;
    try {
      this.setState({audit: await getConversationAudit(this.props.user, this.props.eventId, this.id(selected))});
    } catch (error) {
      this.setState({error: 'The safeguarding audit could not be loaded.'});
    }
  };
  private auditDescription = (item: EventChatAudit) => {
    const details = item.details || {};
    if (item.action === 'RENAME') return `Renamed from “${details.from || 'Private conversation'}” to “${details.to || 'Private conversation'}”.`;
    if (item.action === 'MEMBERS_ADD') return `Added ${(details.members || []).map(member => `${member.name || 'Unknown user'} (${member.role || 'role unknown'})`).join(', ')}.`;
    if (item.action === 'STATUS_CHANGE') return `Status changed from ${details.from} to ${details.to}.`;
    if (item.action === 'ASSIGNMENT_CHANGE') return details.toName || 'Assignment changed.';
    if (item.action === 'READ_RECEIPT_PRIVACY') return details.to ? 'Read receipts were enabled.' : 'Read receipts were hidden.';
    if (item.action === 'SAFEGUARDING_EXPORT') return `Safeguarding record exported as ${details.format || 'PDF'}.`;
    if (item.action === 'REPORT_RESOLVE') return details.resolution === 'REMOVE' ? 'The reported message was reviewed and removed.' : 'The reported message was reviewed and kept.';
    return item.reason || 'No additional details supplied.';
  };
  private exportSafeguarding = async () => {
    if (this.props.onExportSafeguarding) return this.props.onExportSafeguarding();
    const selected = this.state.selected;
    if (!selected) return;
    this.setState({exportingSafeguarding: true});
    try {
      const file = await generateSafeguardingReport(this.props.user, this.props.eventId, this.id(selected));
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `safeguarding-event-${this.props.eventId}-${this.id(selected)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      this.setState({exportingSafeguarding: false});
    } catch (error) {
      this.setState({exportingSafeguarding: false, error: 'The safeguarding PDF could not be generated.'});
    }
  };
  private filteredMentionCandidates = () => this.state.candidates.filter(candidate => `${candidate.label} ${candidate.relatedStudentLabel || ''}`.toLowerCase().includes((this.mentionQuery() || '').toLowerCase())).slice(0, 6);

  private setOverride = async (key: keyof EventCommunicationToggles, value: string) => {
    this.setState({ editingSettings: true, error: '' });
    try {
      await updateEventCommunicationOverrides(this.props.user, this.props.eventId, {
        [key]: value === 'default' ? null : value === 'enabled'
      });
      const config = await getEventCommunicationConfig(this.props.user, this.props.eventId);
      if (key === 'enabled') {
        if (config.eventEnabled === false) {
          this.setState({config, conversations: [], selected: undefined, messages: [], editingSettings: false});
        } else {
          this.setState({editingSettings: false}, this.load);
        }
      } else {
        this.setState({ config, editingSettings: false });
      }
    } catch (error) {
      this.setState({ editingSettings: false, error: 'Chat settings could not be saved.' });
    }
  };

  private setPostingOverride = async (key: 'publicChatParentsPosting' | 'publicChatStudentsPosting', value: string) => {
    this.setState({ editingSettings: true, error: '' });
    try {
      await updateEventCommunicationOverrides(this.props.user, this.props.eventId, {
        [key]: value === 'DEFAULT' ? null : value
      });
      const config = await getEventCommunicationConfig(this.props.user, this.props.eventId);
      this.setState({ config, editingSettings: false });
    } catch (error) {
      this.setState({ editingSettings: false, error: 'Event moderation settings could not be saved.' });
    }
  };

  private saveNotificationPreference = async (key: keyof EventCommunicationPreferences, value: any) => {
    const current = this.state.notificationPreferences;
    if (!current) return;
    const next = {...current, [key]: value};
    this.setState({notificationPreferences: next, savingNotificationPreferences: true, error: ''});
    try {
      const notificationPreferences = await updateEventCommunicationPreferences(this.props.user, next);
      this.setState({notificationPreferences, savingNotificationPreferences: false});
    } catch (error) {
      this.setState({notificationPreferences: current, savingNotificationPreferences: false, error: 'Notification preferences could not be saved.'});
    }
  };

  private setPrivatePolicyOverride = async (key: 'privateParentsPolicy' | 'privateStudentsPolicy', value: string) => {
    this.setState({ editingSettings: true, error: '' });
    try {
      await updateEventCommunicationOverrides(this.props.user, this.props.eventId, {
        [key]: value === 'DEFAULT' ? null : value
      });
      const config = await getEventCommunicationConfig(this.props.user, this.props.eventId);
      this.setState({ config, editingSettings: false });
    } catch (error) {
      this.setState({ editingSettings: false, error: 'Private messaging settings could not be saved.' });
    }
  };

  private saveEdit = async (message: EventChatMessage) => {
    const conversationId = this.state.selected && this.id(this.state.selected);
    const messageId = this.id(message);
    const text = this.state.editingText.trim();
    if (!conversationId || !messageId || !text) return;
    try {
      const updated = await editEventChatMessage(this.props.user, this.props.eventId, conversationId, messageId, text);
      this.setState(state => ({
        messages: state.messages.map(item => (this.id(item) === messageId ? { ...item, ...updated } : item)),
        editingMessageId: undefined,
        editingText: ''
      }));
    } catch (error) {
      this.setState({ error: 'The message could not be edited.' });
    }
  };

  private mentionQuery = () => {
    if (this.state.mentionPickerDismissed) return undefined;
    const match = this.state.text.match(/(?:^|\s)@([^@\s]*)$/);
    return match ? match[1].toLowerCase() : undefined;
  };
  private addMention = (candidate: EventMention) => {
    const text = this.state.text.replace(
      /(?:^|\s)@([^@\s]*)$/,
      match => `${match.startsWith(' ') ? ' ' : ''}@${candidate.label} `
    );
    this.setState(state => ({
      text,
      mentionPickerDismissed: true,
      mentions: state.mentions.some(
        item => item.userId === candidate.userId && item.permissionId === candidate.permissionId
      )
        ? state.mentions
        : [...state.mentions, candidate]
    }), this.focusComposer);
  };

  private focusComposer = () => {
    window.setTimeout(() => {
      const composer = this.composerRef.current;
      if (!composer || composer.disabled) return;
      composer.focus();
      composer.setSelectionRange(composer.value.length, composer.value.length);
    }, 0);
  };

  private renderMessageText = (message: EventChatMessage) => {
    const text = String(message.text || '');
    const mentions = (message.mentions || []).filter(item => item.label);
    if (!mentions.length) return text;
    const byLabel = new Map(mentions.map(item => [String(item.label).toLowerCase(), item]));
    const pattern = mentions.map(item => String(item.label).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length).join('|');
    return text.split(new RegExp(`(@(?:${pattern}))`, 'gi')).map((part, index) => {
      const mention = part.charAt(0) === '@' ? byLabel.get(part.slice(1).toLowerCase()) : undefined;
      return mention
        ? <button key={`${part}:${index}`} type="button" className="eventComms__inlineMention" title="Mention in follow-up" onClick={() => this.mentionInFollowUp({...mention, name: mention.label})}>{part}</button>
        : <React.Fragment key={`${part}:${index}`}>{part}</React.Fragment>;
    });
  };

  private mentionInFollowUp = (reader: {
    userId: string;
    permissionId?: string;
    role?: string;
    name: string;
    avatar?: string;
    roleLabel: string;
  }) => {
    const mention: EventMention = {
      userId: reader.userId,
      permissionId: reader.permissionId,
      role: reader.role,
      label: reader.name,
      avatar: reader.avatar,
      roleLabel: reader.roleLabel
    };
    this.setState(state => ({
      text: `${state.text.trim()}${state.text.trim() ? ' ' : ''}@${reader.name} `,
      mentions: state.mentions.some(item => item.userId === reader.userId && item.permissionId === reader.permissionId)
        ? state.mentions
        : [...state.mentions, mention],
      expandedReceiptMessageId: undefined
    }), this.focusComposer);
  };

  private setReadReceiptPrivacy = async (shareReadReceipts: boolean) => {
    const { selected } = this.state;
    const conversationId = selected && this.id(selected);
    if (!selected || !conversationId) return;
    try {
      await updateReadReceiptPrivacy(this.props.user, this.props.eventId, conversationId, shareReadReceipts);
      this.setState({ selected: { ...selected, viewerReadReceiptPreference: shareReadReceipts } });
    } catch (error) {
      this.setState({ error: 'Read receipt preference could not be saved.' });
    }
  };

  private toggleConversationMute = async () => {
    const selected = this.state.selected;
    if (!selected) return;
    try {
      const result = await updateConversationMute(this.props.user, this.props.eventId, this.id(selected), !selected.viewerMuted);
      this.setState(state => ({selected: {...selected, viewerMuted: result.muted}, conversations: state.conversations.map(item => this.id(item) === this.id(selected) ? {...item, viewerMuted: result.muted} : item), threadMenuOpen: false}));
    } catch (error) { this.setState({error: 'Conversation notification setting could not be saved.'}); }
  };

  private toggleEventMute = async () => {
    const config = this.state.config;
    if (!config) return;
    try {
      const result = await updateEventCommunicationsMute(this.props.user, this.props.eventId, !config.eventMuted);
      this.setState({config: {...config, eventMuted: result.muted}, threadMenuOpen: false});
    } catch (error) { this.setState({error: 'Event notification setting could not be saved.'}); }
  };

  private confirmAction = async () => {
    const { selected, actionMessage, actionType, actionReason, reportConcern } = this.state;
    const conversationId = selected && this.id(selected);
    const messageId = actionMessage && this.id(actionMessage);
    const reason = actionType === 'REPORT' ? `${reportConcern}${actionReason.trim() ? ` — ${actionReason.trim()}` : ''}` : actionReason.trim();
    if (!conversationId || !messageId || !actionType || !reason) return;
    try {
      const updated =
        actionType === 'DELETE'
          ? await deleteEventChatMessage(this.props.user, this.props.eventId, conversationId, messageId, reason)
          : actionType === 'REJECT'
          ? await moderateEventChatMessage(
              this.props.user,
              this.props.eventId,
              conversationId,
              messageId,
              'REJECT',
              reason
            )
          : await reportEventChatMessage(this.props.user, this.props.eventId, conversationId, messageId, reason);
      this.setState(state => ({
        messages: state.messages.map(item => (this.id(item) === messageId ? {
          ...item,
          ...updated,
          ...(actionType === 'REPORT' ? {viewerReported: true} : {})
        } : item)),
        selected: actionType === 'REPORT' && state.selected
          ? {...state.selected, reportedCount: Math.max(1, Number(state.selected.reportedCount) || 0)}
          : state.selected,
        conversations: actionType === 'REPORT'
          ? state.conversations.map(item => this.id(item) === conversationId
            ? {...item, reportedCount: Math.max(1, Number(item.reportedCount) || 0)}
            : item)
          : state.conversations,
        actionMessage: undefined,
        actionType: undefined,
        actionReason: '',
        reportConcern: ''
      }));
    } catch (error) {
      this.setState({
        error: actionType === 'DELETE' ? 'The message could not be removed.' : 'The message could not be reported.'
      });
    }
  };

  private renderSettings() {
    const { config, editingSettings } = this.state;
    if (!config) return null;
    const rows: Array<{ key: keyof EventCommunicationToggles; label: string }> = [
      { key: 'publicChatParentsEnabled', label: 'Event chat for parents' },
      { key: 'publicChatStudentsEnabled', label: 'Event chat for students' }
    ];
    return (
      <section className="eventComms__settings">
        <header>
          <h3>Notification settings</h3>
          <p>Email and push preferences apply across all of your chats.</p>
        </header>
        <a className="eventComms__profileSettingsLink" href="/profile/notifications">Open profile notification settings</a>
        {config.canModerate && <><header><h3>Who can chat on this event</h3><p>Overrides the school&apos;s defaults for this event only.</p></header>
        <div>
          <label className={config.eventOverrides && typeof config.eventOverrides.enabled === 'boolean' ? 'is-overridden' : ''}>
            <span>Chats for this event<small>Switching this off stops new messages for everyone. Existing records are retained for safeguarding.</small></span>
            <select value={config.eventEnabled === false ? 'disabled' : 'enabled'} disabled={editingSettings} onChange={event => this.setOverride('enabled', event.target.value)}>
              <option value="enabled">On</option>
              <option value="disabled">Off</option>
            </select>
          </label>
        </div>
        <div>
          {rows.map(row => {
            const override = config.eventOverrides && config.eventOverrides[row.key];
            const value = typeof override === 'boolean' ? (override ? 'enabled' : 'disabled') : 'default';
            const schoolValue = config.schoolDefaults && config.schoolDefaults[row.key];
            return (
              <label className={typeof override === 'boolean' ? 'is-overridden' : ''} key={row.key}>
                <span>
                  {row.label}
                  <small>School default · {schoolValue ? 'On' : 'Off'}</small>
                </span>
                <select
                  value={value}
                  disabled={editingSettings}
                  onChange={event => this.setOverride(row.key, event.target.value)}
                >
                  <option value="default">School default</option>
                  <option value="enabled">On · overridden</option>
                  <option value="disabled">Off · overridden</option>
                </select>
              </label>
            );
          })}
        </div>
        {([
          { key: 'privateParentsPolicy', label: 'Private chats with parents' },
          { key: 'privateStudentsPolicy', label: 'Private chats with students' }
        ] as const).map(row => {
          const override = config.eventOverrides && config.eventOverrides[row.key];
          const schoolValue = (config.schoolDefaults && config.schoolDefaults[row.key]) || 'DISABLED';
          return (
            <label className={override ? 'is-overridden' : ''} key={row.key}>
              <span>
                {row.label}
                <small>School default · {schoolValue === 'TWO_WAY' ? 'Can reply' : schoolValue === 'STAFF_INITIATED' ? 'Read only' : 'Off'}</small>
              </span>
              <div className="eventComms__select">
                <select
                  value={override || 'DEFAULT'}
                  disabled={editingSettings}
                  onChange={event => this.setPrivatePolicyOverride(row.key, event.target.value)}
                >
                  <option value="DEFAULT">School default</option>
                  <option value="DISABLED">Off · overridden</option>
                  <option value="STAFF_INITIATED">Read only · overridden</option>
                  <option value="TWO_WAY">Can reply · overridden</option>
                </select>
              </div>
            </label>
          );
        })}
        {([
          {
            key: 'publicChatParentsPosting',
            accessKey: 'publicChatParentsEnabled',
            label: 'Parents’ messages'
          },
          {
            key: 'publicChatStudentsPosting',
            accessKey: 'publicChatStudentsEnabled',
            label: 'Students’ messages'
          }
        ] as const).map(row => {
          const override = config.eventOverrides && config.eventOverrides[row.key];
          const schoolValue = (config.schoolDefaults && config.schoolDefaults[row.key]) || 'IMMEDIATE';
          const accessOverride = config.eventOverrides && config.eventOverrides[row.accessKey];
          const hasAccess =
            typeof accessOverride === 'boolean'
              ? accessOverride
              : Boolean(config.schoolDefaults && config.schoolDefaults[row.accessKey]);
          return (
            <label className={`${!hasAccess ? 'is-disabled' : ''} ${override ? 'is-overridden' : ''}`} key={row.key}>
              <span>
                {row.label}
                <small>{hasAccess ? `School default · ${schoolValue === 'MODERATED' ? 'Approved by staff' : schoolValue === 'DISABLED' ? 'Read only' : 'Post immediately'}` : 'Turn event chat on for this role first'}</small>
              </span>
              <div className="eventComms__select">
                <select
                  value={override || 'DEFAULT'}
                  disabled={editingSettings || !hasAccess}
                  onChange={event => this.setPostingOverride(row.key, event.target.value)}
                >
                  <option value="DEFAULT">School default</option>
                  <option value="IMMEDIATE">Post immediately · overridden</option>
                  <option value="MODERATED">Approved by staff · overridden</option>
                  <option value="DISABLED">Read only · overridden</option>
                </select>
              </div>
            </label>
          );
        })}
        </>}
      </section>
    );
  }

  render() {
    const {
      config,
      conversations,
      selected,
      messages,
      loading,
      sending,
      error,
      text,
      replyTo,
      candidates,
      mentions,
      editingMessageId,
      editingText,
      actionMessage,
      actionType,
      actionReason,
      loadingOlder,
      hasOlderMessages
    } = this.state;
    const canMention = Boolean(config && config.canModerate);
    const activePanel = this.props.panel || this.state.activePanel;
    if (loading) {
      return (
        <section className="eventComms eventComms--loading" aria-label="Loading communications">
          <div />
          <div />
          <div />
        </section>
      );
    }
    if (!config) return null;
    if (config.available === false) {
      const isStaff = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'].includes(this.props.user.activeRole);
      return (
        <section className="eventComms eventComms--unavailable eventComms--notIncluded">
          <div className="eventComms__emptyIcon">✦</div>
          <h3>{isStaff ? 'Bring event conversations into one secure place' : 'Chats are not available'}</h3>
          <p>{isStaff ? 'This feature is not enabled for your school yet. Contact Squad In Touch to discuss access for staff, parents and students.' : 'Your school has not enabled this feature. Please contact the school if you need help with this event.'}</p>
          {isStaff && <a href="mailto:support@squadintouch.com?subject=Enable%20Chats">Contact support</a>}
        </section>
      );
    }
    if (config.eventEnabled === false && activePanel !== 'SETTINGS' && !(this.props.threadOnly && ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'].includes(this.props.user.activeRole))) {
      const isStaff = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'].includes(this.props.user.activeRole);
      return (
        <section className="eventComms eventComms--unavailable">
          <div className="eventComms__emptyIcon">⌁</div>
          <h3>Chats are switched off for this event</h3>
          <p>{isStaff ? 'Existing records are retained for safeguarding. Open Notification settings to switch chats back on.' : 'Your school has switched off chats for this event.'}</p>
        </section>
      );
    }
    if (!config.publicChatEnabled && !(config.privateMessagesAccessible || config.privateMessagesEnabled) && activePanel !== 'SETTINGS') {
      return (
        <section className="eventComms eventComms--unavailable">
          <div className="eventComms__emptyIcon">⌁</div>
          <h3>Chats are not available for this event</h3>
          <p>Your school controls access to event chats and private messages.</p>
        </section>
      );
    }
    return (
      <section className={`eventComms ${this.props.threadOnly ? 'eventComms--threadOnly' : ''} ${activePanel === 'SETTINGS' ? 'eventComms--settings' : ''}`} aria-label="Chats">
        <div className="eventComms__header">
          <div className="eventComms__actions">
            {activePanel === 'CHATS' && config.canModerate && (
              <button type="button" onClick={this.openRecipientPicker}>
                New private chat
              </button>
            )}
            {activePanel === 'CHATS' && config.privateMessagesEnabled && !config.canModerate && (
              <>
                {this.props.subjects && this.props.subjects.length > 0 && (
                  <div className="eventComms__subjectSelect">
                    <select
                      aria-label="Student"
                      value={this.state.selectedSubjectPermissionId || ''}
                      onChange={event => this.setState({ selectedSubjectPermissionId: event.target.value })}
                    >
                      <option value="">General question</option>
                      {this.props.subjects.map(subject => (
                        <option key={subject.permissionId} value={subject.permissionId}>
                          {subject.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <input className="eventComms__newChatTitle" maxLength={80} value={this.state.conversationTitle} onChange={event => this.setState({conversationTitle: event.target.value})} placeholder="Conversation name (optional)" aria-label="Conversation name" />
                <button
                  type="button"
                  onClick={() => this.start('PRIVATE')}
                >
                  Message event staff
                </button>
              </>
            )}
          </div>
        </div>
        {error && (
          <div className="eventComms__error" role="alert">
            {error}
          </div>
        )}
        {this.state.notice && (
          <div className="eventComms__notice" role="status">
            <span aria-hidden="true">i</span>
            <p>{this.state.notice}</p>
            <button type="button" aria-label="Dismiss" onClick={() => this.setState({ notice: '' })}>×</button>
          </div>
        )}
        {activePanel === 'SETTINGS' ? (
          this.renderSettings()
        ) : (
          <>
            <div className="eventComms__layout">
              <nav className="eventComms__list" aria-label="Conversations">
                {this.sortConversations(conversations).map(item => (
                  <button
                    type="button"
                    className={this.id(selected || {}) === this.id(item) ? 'is-active' : ''}
                    key={this.id(item)}
                    onClick={() => this.open(item)}
                  >
                    <span className={`eventComms__conversationIcon is-${item.type.toLowerCase()}`}>
                      {item.type === 'PUBLIC' ? '#' : '🔒'}
                    </span>
                    <span className="eventComms__conversationCopy">
                      <strong title={this.conversationLabel(item)}>
                        {this.conversationLabel(item)}
                      </strong>
                      <span className="eventComms__conversationChips">
                        <small className={`is-type is-${item.type.toLowerCase()}`}>{item.type === 'PUBLIC' ? 'Event chat' : 'Private'}</small>
                        {item.type === 'PRIVATE' && item.memberCount && <small className="is-people">{item.memberCount} people</small>}
                        {item.type === 'PRIVATE' && <small className="is-initiator">{this.conversationInitiatorLabel(item)}</small>}
                        {item.subjectIsParticipating === false && <small className="is-reply">No longer participating</small>}
                        {config.canModerate && item.unanswered && <small className="is-reply">Needs reply</small>}
                      </span>
                      <em>{item.lastMessagePreview || 'No messages yet'}</em>
                    </span>
                    {Number(item.unreadCount) > 0 && <b className="eventComms__unread">{item.unreadCount}</b>}
                  </button>
                ))}
              </nav>
              <div className="eventComms__thread">
                {!selected ? (
                  <p className="eventComms__empty">Choose a conversation to view messages.</p>
                ) : (
                  <>
                    <div className="eventComms__threadControls">
                      <div className="eventComms__threadIdentity">
                        <h3
                          title={this.conversationLabel(selected)}
                        >
                          {this.conversationLabel(selected)}
                        </h3>
                        <span>{selected.type === 'PUBLIC' ? 'Everyone on this event' : 'Private chat'}{selected.memberCount ? ` · ${selected.memberCount} people` : ''}</span>
                      </div>
                      <div className="eventComms__threadTools">
                        <button type="button" className="eventComms__participantsButton" onClick={this.openParticipants}><span>{selected.memberCount || '—'}</span> people</button>
                        <button type="button" className="eventComms__menuButton" aria-label="Conversation menu" aria-expanded={Boolean(this.state.threadMenuOpen)} onClick={() => this.setState(state => ({threadMenuOpen: !state.threadMenuOpen}))}>⋯</button>
                        {this.state.threadMenuOpen && <div className="eventComms__threadMenu">
                          {this.props.onToggleAssignment && <button type="button" onClick={this.props.onToggleAssignment}><b>{this.props.assignedToMe ? 'Unassign me' : 'Assign to me'}</b><span>{this.props.assignedToMe ? 'Move it back to the team queue' : 'Show it under Assigned to me'}</span></button>}
                          <button type="button" onClick={this.openParticipants}><b>Participants</b><span>{selected.memberCount || 'View'} people</span></button>
                          <button type="button" onClick={this.toggleConversationMute}><b>{selected.viewerMuted ? 'Unmute conversation' : 'Mute this conversation'}</b><span>{selected.viewerMuted ? 'Resume optional email and push alerts' : 'Keep it in your inbox without optional alerts'}</span></button>
                          <button type="button" onClick={this.toggleEventMute}><b>{config.eventMuted ? 'Unmute this event' : 'Mute this event'}</b><span>{config.eventMuted ? 'Resume alerts for every chat on this event' : 'Silence optional alerts for all chats on this event'}</span></button>
                          {selected.type === 'PRIVATE' && (config.canModerate || String(selected.createdByUserId) === String(this.props.user.userId) && !selected.staffRenamedTitle) && <button type="button" onClick={this.openParticipants}><b>Rename chat</b><span>Change the name shown to participants</span></button>}
                          {config.canModerate && <button type="button" onClick={this.showAudit}><b>Audit trail</b><span>Every message, edit, removal and report</span></button>}
                          {['ADMIN','MANAGER'].includes(this.props.user.activeRole) && <button type="button" disabled={Boolean(this.props.exportingSafeguarding || this.state.exportingSafeguarding)} onClick={this.exportSafeguarding}><b>{this.props.exportingSafeguarding || this.state.exportingSafeguarding ? 'Preparing PDF…' : 'Export for safeguarding'}</b><span>Download the full history and audit as PDF</span></button>}
                          {config.canModerate && <label className="eventComms__receiptToggle"><span><b>Share my read receipts</b><small>Let participants see when you have read messages</small></span><input type="checkbox" checked={selected.viewerReadReceiptPreference !== false} onChange={event => this.setReadReceiptPrivacy(event.target.checked)} /></label>}
                          {config.canModerate && <label className="eventComms__conversationStatus"><span><b>Conversation status</b></span><select value={selected.status} onChange={async event => { const updated = await updateConversationStatus(this.props.user, this.props.eventId, this.id(selected), event.target.value); this.setState({selected: {...selected, ...updated}}); }}><option value="OPEN">Open</option><option value="READ_ONLY">Staff announcements only</option><option value="CLOSED">Resolved</option></select></label>}
                        </div>}
                      </div>
                    </div>
                    <div className="eventComms__messages" ref={this.messagesRef}>
                      {hasOlderMessages && (
                        <button
                          type="button"
                          className="eventComms__loadOlder"
                          disabled={loadingOlder}
                          onClick={this.loadOlderMessages}
                        >
                          {loadingOlder ? 'Loading earlier messages…' : 'Load earlier messages'}
                        </button>
                      )}
                      {!messages.length && <div className="eventComms__emptyThread"><strong>No messages yet</strong><span>{selected.memberCount ? `${selected.memberCount} people are in this chat. ` : ''}They&apos;ll be notified when you post the first message.</span></div>}
                      {messages.map((message, messageIndex) => {
                        const activePermissionId =
                          this.props.user.activePermission && this.props.user.activePermission.id;
                        const own =
                          String(message.authorUserId) === String(this.props.user.userId) &&
                          String(message.authorPermissionId || '') === String(activePermissionId || '') &&
                          message.authorRole === this.props.user.activeRole;
                        const isMine = String(message.authorUserId) === String(this.props.user.userId);
                        const display = message.authorDisplay;
                        const receipts = message.receiptSummary;
                        const pending = message.publicationStatus === 'PENDING';
                        const authorName =
                          (display && display.name) ||
                          (message.author && message.author.fullName) ||
                          message.authorRole;
                        const activeUser: any = this.props.user;
                        const avatar =
                          (message.author && message.author.avatar) ||
                          (own && ((activeUser.profile && activeUser.profile.avatar) || activeUser.avatar));
                        const receiptsExpanded = this.state.expandedReceiptMessageId === this.id(message);
                        const previous = messages[messageIndex - 1];
                        const showDay = !previous || new Date(previous.createdAt).toDateString() !== new Date(message.createdAt).toDateString();
                        return (
                          <React.Fragment key={this.id(message)}>
                          {showDay && <div className="eventComms__daySeparator"><span>{this.dayLabel(message.createdAt)}</span></div>}
                          <article
                            id={`event-message-${this.id(message)}`}
                            className={`eventComms__message ${isMine ? 'is-own' : ''} ${pending ? 'is-pending' : ''} ${message.reportSummary ? 'is-reported' : ''} ${message.viewerReported ? 'is-reported-by-viewer' : ''}`}
                          >
                            <div className={`eventComms__avatar ${avatar ? 'has-image' : ''}`} aria-hidden="true">
                              {avatar ? (
                                <img src={avatar} alt="" />
                              ) : (
                                String(authorName)
                                  .trim()
                                  .split(/\s+/)
                                  .slice(0, 2)
                                  .map(part => part.charAt(0))
                                  .join('')
                                  .toUpperCase()
                              )}
                            </div>
                            <div className="eventComms__bubble">
                              {message.replyTo && <blockquote><strong>{message.replyTo.authorName || 'Participant'}</strong><span>{message.replyTo.authorRole || ''}</span><p>{message.replyTo.text}</p></blockquote>}
                              {message.reportSummary && (
                                <div className="eventComms__reportAlert" role="alert">
                                  <strong>Reported for staff review</strong>
                                  <span>{message.reportSummary.count} report{message.reportSummary.count === 1 ? '' : 's'}</span>
                                  {message.reportSummary.reports.map((report, index) => <p key={`${report.createdAt}-${index}`}>Reported by {report.reportedBy || 'a participant'} · “{report.reason}”</p>)}
                                  <div><button type="button" onClick={async () => { const updated = await resolveEventChatMessageReport(this.props.user, this.props.eventId, this.id(selected), this.id(message), 'KEEP'); this.setState(state => ({messages: state.messages.map(item => this.id(item) === this.id(message) ? {...item, ...updated} : item)})); }}>Keep message</button><button type="button" onClick={async () => { const updated = await resolveEventChatMessageReport(this.props.user, this.props.eventId, this.id(selected), this.id(message), 'REMOVE'); this.setState(state => ({messages: state.messages.map(item => this.id(item) === this.id(message) ? {...item, ...updated} : item)})); }}>Remove message</button></div>
                                </div>
                              )}
                              {message.viewerReported && !message.reportSummary && <div className="eventComms__viewerReportNote"><strong>Reported to staff</strong><span>Your concern has been recorded for review.</span></div>}
                              <div className="eventComms__author">
                                <strong>{authorName}</strong>
                                <span
                                  className={`eventComms__role eventComms__role--${String(
                                    message.authorRole
                                  ).toLowerCase()}`}
                                >
                                  {(display && display.roleLabel) || message.authorRole}
                                </span>
                                <time>
                                  {new Date(message.createdAt).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </time>
                                {message.publicationStatus === 'REJECTED' && (
                                  <span className="eventComms__moderationBadge is-rejected">Rejected</span>
                                )}
                              </div>
                              {message.authorRole === 'PARENT' && message.relatedStudents && message.relatedStudents.length > 0 && (() => {
                                const messageId = this.id(message);
                                const expanded = this.state.expandedParentMessageIds.includes(messageId);
                                const visibleChildren = expanded ? message.relatedStudents : message.relatedStudents.slice(0, 1);
                                const hiddenCount = message.relatedStudents.length - visibleChildren.length;
                                return <div className="eventComms__childContext">
                                  <span>Regarding</span>
                                  {visibleChildren.map(student => <b key={student.userId}>{student.name}</b>)}
                                  {(hiddenCount > 0 || expanded && message.relatedStudents.length > 1) && <button type="button" onClick={() => this.setState(state => ({expandedParentMessageIds: expanded ? state.expandedParentMessageIds.filter(id => id !== messageId) : [...state.expandedParentMessageIds, messageId]}))}>{expanded ? 'Show less' : `+${hiddenCount}`}</button>}
                                </div>;
                              })()}
                              {editingMessageId === this.id(message) ? (
                                <div className="eventComms__edit">
                                  <textarea
                                    autoFocus
                                    value={editingText}
                                    maxLength={4000}
                                    onChange={event => this.setState({ editingText: event.target.value })}
                                  />
                                  <div>
                                    <button
                                      className="is-secondary"
                                      type="button"
                                      onClick={() => this.setState({ editingMessageId: undefined })}
                                    >
                                      Cancel
                                    </button>
                                    <button className="is-primary" type="button" onClick={() => this.saveEdit(message)}>
                                      Save changes
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <p>
                                  {message.deletedAt ? 'Message removed' : this.renderMessageText(message)}{' '}
                                  {message.editedAt && <small>(edited)</small>}
                                </p>
                              )}
                              {message.publicationStatus === 'REJECTED' && message.rejectionReason && (
                                <small>Reason: {message.rejectionReason}</small>
                              )}
                              {pending && <div className="eventComms__pendingNote">{config.canModerate ? `Waiting for approval — only staff and ${authorName.split(' ')[0]} can see this.` : 'Waiting for staff approval. Only you and staff can see it.'}</div>}
                              {pending && config.canModerate && (
                                <div className="eventComms__moderationActions">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const conversationId = this.id(selected);
                                      const messageId = this.id(message);
                                      const updated = await moderateEventChatMessage(
                                        this.props.user,
                                        this.props.eventId,
                                        conversationId,
                                        messageId,
                                        'APPROVE'
                                      );
                                      this.setState(state => ({
                                        messages: state.messages.map(item =>
                                          this.id(item) === messageId ? { ...item, ...updated } : item
                                        )
                                      }));
                                    }}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      this.setState({ actionMessage: message, actionType: 'REJECT', actionReason: '' })
                                    }
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                              {own && receipts && message.publicationStatus !== 'PENDING' && (
                                <div className="eventComms__receipts">
                                  <button
                                    type="button"
                                    disabled={!receipts.readers && !receipts.unreadReaders}
                                    onClick={() =>
                                      this.setState({
                                        expandedReceiptMessageId: receiptsExpanded ? undefined : this.id(message)
                                      })
                                    }
                                  >
                                    {receipts.readCount
                                      ? `Read by ${receipts.readCount}${
                                          receipts.unreadCount ? ` · ${receipts.unreadCount} not read` : ''
                                        }`
                                      : receipts.unreadCount
                                      ? `Not read by ${receipts.unreadCount}`
                                      : 'Sent'}
                                  </button>
                                  {receipts.privacyProtectedCount > 0 && <span> · Some read statuses are private</span>}
                                  {receiptsExpanded && (receipts.readers || receipts.unreadReaders) && (
                                    <div className="eventComms__receiptDetails">
                                      {!!receipts.readers && !!receipts.readers.length && <h5>Read</h5>}
                                      {(receipts.readers || []).map(reader => (
                                        <div key={`read:${reader.userId}:${reader.roleLabel}`}>
                                          <b>{reader.name}</b>
                                          <span>
                                            {reader.roleLabel}
                                            {reader.readAt ? ` · ${new Date(reader.readAt).toLocaleString()}` : ''}
                                          </span>
                                        </div>
                                      ))}
                                      {!!receipts.unreadReaders && !!receipts.unreadReaders.length && (
                                        <h5>Not read yet</h5>
                                      )}
                                      {(receipts.unreadReaders || []).map(reader => (
                                        <div key={`unread:${reader.userId}:${reader.roleLabel}`}>
                                          <b>
                                            {reader.name}
                                            <small>{reader.roleLabel}</small>
                                          </b>
                                          <button type="button" onClick={() => this.mentionInFollowUp(reader)}>
                                            Mention in follow-up
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {!message.deletedAt && !pending && (
                                <div className="eventComms__messageActions">
                                  <button type="button" onClick={() => this.setState({ replyTo: message })}>
                                    Reply
                                  </button>
                                  {own && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        this.setState({ editingMessageId: this.id(message), editingText: message.text })
                                      }
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {(!isMine || own || config.canModerate) && <button
                                    type="button"
                                    disabled={!own && !config.canModerate && message.viewerReported}
                                    onClick={() =>
                                      this.setState({
                                        actionMessage: message,
                                        actionType: own || config.canModerate ? 'DELETE' : 'REPORT',
                                        actionReason: ''
                                      })
                                    }
                                  >
                                    {own || config.canModerate ? 'Remove' : message.viewerReported ? 'Reported' : 'Report'}
                                  </button>}
                                  {config.canModerate && !isMine && <button type="button" onClick={() => this.setState({actionMessage: message, actionType: 'REPORT', actionReason: '', reportConcern: ''})}>Report</button>}
                                </div>
                              )}
                            </div>
                          </article>
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {config.eventEnabled === false ? (
                      <div className="eventComms__closedState">
                        <strong>Chats are switched off for this event</strong>
                        <span>The history is preserved for safeguarding, but no new messages can be sent.</span>
                      </div>
                    ) : selected.status === 'CLOSED' || selected.status === 'ARCHIVED' ? (
                      <div className="eventComms__closedState">
                        <strong>Conversation resolved</strong>
                        <span>The history is preserved and no new messages can be sent.</span>
                        {config.canModerate && (
                          <button
                            type="button"
                            onClick={async () => {
                              const updated = await updateConversationStatus(
                                this.props.user,
                                this.props.eventId,
                                this.id(selected),
                                'OPEN'
                              );
                              this.setState({ selected: { ...selected, ...updated } });
                            }}
                          >
                            {selected.status === 'ARCHIVED' ? 'Restore conversation' : 'Reopen conversation'}
                          </button>
                        )}
                      </div>
                    ) : selected.type !== 'PUBLIC' || config.canModerate || config.postingPolicy !== 'DISABLED' ? (
                      <form className="eventComms__composer" onSubmit={this.send}>
                        <div className="eventComms__composerStatus"><i />{this.composerStatus(selected, config)}</div>
                        <div>
                          {replyTo && (
                            <div className="eventComms__reply">
                              <span><strong>Replying to {replyTo.authorDisplay && replyTo.authorDisplay.name || replyTo.author && replyTo.author.fullName || 'participant'}</strong><small>{replyTo.text}</small></span>
                              <button type="button" onClick={() => this.setState({ replyTo: undefined })}>
                                ×
                              </button>
                            </div>
                          )}
                          {canMention && typeof this.mentionQuery() === 'string' && (
                            <div className="eventComms__mentions">
                              <header>{selected.type === 'PRIVATE' ? 'People in this chat' : 'Event participants and staff'}</header>
                              {this.filteredMentionCandidates().map((candidate, candidateIndex) => (
                                  <button
                                    type="button"
                                    className={candidateIndex === this.state.mentionActiveIndex ? 'is-active' : ''}
                                    key={`${candidate.userId}:${candidate.permissionId}`}
                                    onClick={() => this.addMention(candidate)}
                                  >
                                    <span className="eventComms__mentionIdentity">
                                      <b>{candidate.label}</b>
                                      {candidate.relatedStudentLabel && (
                                        <small>Parent of {candidate.relatedStudentLabel}</small>
                                      )}
                                    </span>
                                    {candidate.roleLabel && <em>{candidate.roleLabel}</em>}
                                  </button>
                                ))}
                            </div>
                          )}
                          <textarea
                            ref={this.composerRef}
                            value={text}
                            maxLength={4000}
                            disabled={sending || (selected.status === 'READ_ONLY' && !config.canModerate)}
                            onKeyDown={this.composerKeyDown}
                            onChange={event => {
                              event.currentTarget.style.height = 'auto';
                              event.currentTarget.style.height = `${Math.min(140, event.currentTarget.scrollHeight)}px`;
                              this.setState({
                                text: event.target.value,
                                mentionPickerDismissed: false,
                                mentionActiveIndex: 0,
                                mentions: canMention
                                  ? mentions.filter(item => event.target.value.includes(`@${item.label}`))
                                  : []
                              });
                            }}
                            placeholder={
                              canMention
                                ? 'Write a message — type @ to mention someone'
                                : selected.type === 'PRIVATE' ? 'Write a private message to event staff' : 'Write a message'
                            }
                          />
                          <small className="eventComms__keyboardHint">Enter to send · Shift+Enter for a new line</small>
                        </div>
                        <button
                          type="submit"
                          disabled={!text.trim() || sending || (selected.status === 'READ_ONLY' && !config.canModerate)}
                        >
                          Send
                        </button>
                      </form>
                    ) : (
                      <p className="eventComms__postingDisabled">
                        Staff announcements only. You can read messages, but only staff can post.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {actionMessage && (
          <AccessibleModal backdropClassName="eventComms__modalBackdrop" className={`eventComms__actionPanel ${actionType === 'REPORT' ? 'is-report' : ''}`} labelledBy="event-comms-action-title" onClose={() => this.setState({ actionMessage: undefined, actionType: undefined, actionReason: '', reportConcern: '' })}>
              <h3 id="event-comms-action-title">
                {actionType === 'DELETE'
                  ? 'Remove message'
                  : actionType === 'REJECT'
                  ? 'Reject message'
                  : 'Report this message'}
              </h3>
              <button className="eventComms__modalClose" type="button" aria-label="Close" onClick={() => this.setState({actionMessage: undefined, actionType: undefined, actionReason: '', reportConcern: ''})}>×</button>
              <p>
                {actionType === 'DELETE'
                  ? 'The message will be hidden but retained in the safeguarding audit.'
                  : actionType === 'REJECT'
                  ? 'Give the author a clear reason why this message was not published.'
                  : 'You are asking authorised school staff to review this message as a safeguarding or conduct concern.'}
              </p>
              {actionType === 'REPORT' && (
                <><blockquote className="eventComms__reportedMessage"><strong>{(actionMessage.authorDisplay && actionMessage.authorDisplay.name) || (actionMessage.author && actionMessage.author.fullName) || actionMessage.authorRole} · {(actionMessage.authorDisplay && actionMessage.authorDisplay.roleLabel) || actionMessage.authorRole} · {new Date(actionMessage.createdAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</strong><span>{actionMessage.text}</span></blockquote><fieldset className="eventComms__concerns"><legend>What&apos;s the concern?</legend>{['Bullying or unkind','Inappropriate content','Safeguarding concern','Spam or off-topic','Something else'].map(reason => <button type="button" className={this.state.reportConcern === reason ? 'is-selected' : ''} key={reason} onClick={() => this.setState({reportConcern: reason})}>{reason}</button>)}</fieldset></>
              )}
              <label>
                {actionType === 'REPORT' ? <>Details <small>(optional)</small></> : 'Reason for this action'}
                <textarea
                  autoFocus={actionType !== 'REPORT'}
                  value={actionReason}
                  maxLength={500}
                  onChange={event => this.setState({ actionReason: event.target.value })}
                  placeholder={actionType === 'REPORT' ? 'Add anything that will help staff understand what happened.' : 'Give enough detail for staff to understand the concern'}
                />
              </label>
              {actionType === 'REPORT' && <div className="eventComms__reportWarning"><strong>Your report is recorded.</strong><span>Your name, role, the reason and the original message are kept in the safeguarding audit.</span></div>}
              <div>
                <button
                  className="is-secondary"
                  type="button"
                  onClick={() => this.setState({ actionMessage: undefined, actionType: undefined, actionReason: '', reportConcern: '' })}
                >
                  Cancel
                </button>
                <button
                  className="is-danger"
                  type="button"
                  disabled={actionType === 'REPORT' ? !this.state.reportConcern : !actionReason.trim()}
                  onClick={this.confirmAction}
                >
                  {actionType === 'REPORT' ? 'Send report' : 'Confirm action'}
                </button>
              </div>
          </AccessibleModal>
        )}
        {this.state.recipientPickerOpen && (
          <AccessibleModal backdropClassName="eventComms__modalBackdrop" className="eventComms__recipientModal" labelledBy="event-comms-recipient-title" onClose={() => this.setState({ recipientPickerOpen: false })}>
              <header>
                <div>
                  <h3 id="event-comms-recipient-title">New private chat</h3>
                  <p>Only the people you add, and authorised event staff, can see it.</p>
                </div>
                <button type="button" aria-label="Close" onClick={() => this.setState({ recipientPickerOpen: false })}>
                  ×
                </button>
              </header>
              <div className="eventComms__recipientFields">
                <label>Chat name <small>(optional)</small></label>
                <input
                  maxLength={120}
                  value={this.state.conversationTitle}
                  onChange={event => this.setState({ conversationTitle: event.target.value })}
                  placeholder="e.g. Lifts to Beare Green"
                />
                <label>People</label>
                {!!this.state.selectedRecipients.length && <div className="eventComms__selectedRecipients">{this.state.selectedRecipients.map(item => <button type="button" key={`${item.userId}:${item.permissionId}:${item.role}`} onClick={() => this.toggleRecipient(item)}>{item.label}<span>×</span></button>)}</div>}
                <div>
                  <input
                    autoFocus
                    type="search"
                    value={this.state.recipientSearch}
                    onChange={event => this.setState({ recipientSearch: event.target.value })}
                    placeholder="Search by name, child or form"
                  />
                  <div className="eventComms__roleFilters" role="tablist" aria-label="Filter people by role">{[['ALL','Everyone'],['PARENT','Parents'],['STUDENT','Students'],['STAFF','Staff']].map(([value,label]) => <button role="tab" aria-selected={this.state.recipientRole === value} className={this.state.recipientRole === value ? 'is-active' : ''} type="button" key={value} onClick={() => this.setState({recipientRole: value})}>{label}</button>)}</div>
                </div>
              </div>
              <div className="eventComms__recipientList">
                {this.recipientGroups().map(group => <section key={group.key}><div className="eventComms__recipientGroupHeader"><span>{group.label}</span><button type="button" onClick={() => this.selectRecipients(group.items)}>Add all {group.items.length}</button></div>{group.items.map(item => {
                  const selectedRecipient = this.state.selectedRecipients.some(
                    value => value.userId === item.userId && value.permissionId === item.permissionId && value.role === item.role
                  );
                  const children = item.relatedStudentLabels || [];
                  return (
                    <button
                      type="button"
                      className={selectedRecipient ? 'is-selected' : ''}
                      key={`${item.userId}:${item.permissionId || ''}:${item.role}`}
                      onClick={() => this.toggleRecipient(item)}
                    >
                      <span className={`eventComms__recipientAvatar ${item.avatar ? 'has-image' : ''}`}>
                        {item.avatar ? (
                          <img src={item.avatar} alt="" />
                        ) : (
                          item.label
                            .split(/\s+/)
                            .slice(0, 2)
                            .map(part => part.charAt(0))
                            .join('')
                        )}
                      </span>
                      <span>
                        <strong>{item.label}</strong>
                        <small>
                          {children.length
                            ? `Parent of ${children.slice(0, 2).join(', ')}${
                                children.length > 2 ? ` +${children.length - 2}` : ''
                              }`
                            : item.roleLabel}
                        </small>
                      </span>
                      <i>{selectedRecipient ? '✓' : '+'}</i>
                    </button>
                  );
                })}</section>)}
              </div>
              <footer>
                <span>{this.state.selectedRecipients.length} people selected</span>
                <button type="button" onClick={() => this.setState({ recipientPickerOpen: false })}>
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!this.state.selectedRecipients.length}
                  onClick={this.createStaffPrivateConversation}
                >
                  Create chat
                </button>
              </footer>
          </AccessibleModal>
        )}
        {this.state.audit && (
          <AccessibleModal backdropClassName="eventComms__modalBackdrop" className="eventComms__auditModal" labelledBy="event-comms-audit-title" onClose={() => this.setState({audit: undefined})}>
            <header>
              <div><h3 id="event-comms-audit-title">Safeguarding audit</h3><p>Recorded changes and safeguarding actions for this conversation.</p></div>
              <button type="button" aria-label="Close" onClick={() => this.setState({audit: undefined})}>×</button>
            </header>
            <div className="eventComms__auditBody">
              {!this.state.audit.length && <p className="eventComms__auditEmpty">No audit activity has been recorded yet.</p>}
              {this.state.audit.map(item => <article key={item.id || item._id}>
                <div><strong>{item.action.replace(/_/g, ' ')}</strong><time>{new Date(item.createdAt).toLocaleString()}</time></div>
                <p>{this.auditDescription(item)}</p>
                {item.originalText && <blockquote>{item.originalText}</blockquote>}
              </article>)}
            </div>
            <footer><button type="button" className="is-primary" onClick={() => this.setState({audit: undefined})}>Close</button></footer>
          </AccessibleModal>
        )}
        {this.state.participantsOpen && (
          <AccessibleModal backdropClassName="eventComms__modalBackdrop" className="eventComms__participantsModal" labelledBy="event-comms-participants-title" onClose={() => this.setState({ participantsOpen: false })}>
              <header>
                <div>
                  <h3 id="event-comms-participants-title">Conversation participants</h3>
                  <p>Authorised event staff retain safeguarding access even when they are not listed here.</p>
                </div>
                <button type="button" aria-label="Close" onClick={() => this.setState({ participantsOpen: false })}>
                  ×
                </button>
              </header>
              <div className="eventComms__participantsBody">
              {selected && selected.type === 'PRIVATE' && (config.canModerate || String(selected.createdByUserId) === String(this.props.user.userId) && !selected.staffRenamedTitle) && (
                <label className="eventComms__conversationNameField">
                  <span>Conversation name</span>
                  <input
                    maxLength={80}
                    value={this.state.conversationTitle}
                    onChange={event => this.setState({ conversationTitle: event.target.value })}
                    placeholder="Private conversation"
                  />
                  {!config.canModerate && <small>Staff can take ownership of this name by renaming the conversation.</small>}
                </label>
              )}
              {config.canManageSafeguardingPolicy && selected && (
                <section className="eventComms__legalHold">
                  <h4>Protect this conversation from deletion</h4>
                  <p>{selected.legalHold ? 'Automatic deletion is currently prevented for this conversation. Remove the protection when the safeguarding concern, complaint or legal case is closed.' : 'Turn this on when the conversation is needed for an active safeguarding concern, complaint or legal case. It will not be automatically deleted until the protection is removed.'}</p>
                  {!selected.legalHold && <textarea maxLength={500} value={this.state.legalHoldReason} onChange={event => this.setState({legalHoldReason: event.target.value})} placeholder="Explain why this conversation must be protected" />}
                  <button type="button" className={selected.legalHold ? 'is-danger' : 'is-primary'} disabled={!selected.legalHold && !this.state.legalHoldReason.trim()} onClick={() => this.setLegalHold(!selected.legalHold)}>
                    {selected.legalHold ? 'Remove protection' : 'Prevent automatic deletion'}
                  </button>
                </section>
              )}
              {config.canModerate && selected && selected.type === 'PRIVATE' && (
                <section className="eventComms__archiveControl">
                  <h4>{selected.status === 'ARCHIVED' ? 'Archived conversation' : 'Archive conversation'}</h4>
                  <p>{selected.status === 'ARCHIVED' ? 'This conversation is hidden from the active queue and remains available for safeguarding.' : 'Archive a historical conversation without deleting its messages or audit history.'}</p>
                  <button type="button" className="eventComms__archiveButton" onClick={async () => { const status = selected.status === 'ARCHIVED' ? 'OPEN' : 'ARCHIVED'; const updated = await updateConversationStatus(this.props.user, this.props.eventId, this.id(selected), status); this.setState(state => ({selected: {...selected, ...updated}, conversations: state.conversations.map(item => this.id(item) === this.id(selected) ? {...item, ...updated} : item), participantsOpen: false})); }}>{selected.status === 'ARCHIVED' ? 'Restore to active chats' : 'Archive conversation'}</button>
                </section>
              )}
              <h4>Conversation participants</h4>
              <div className={`eventComms__participantChips ${this.state.participantsExpanded ? 'is-expanded' : 'is-collapsed'}`}>
                {this.state.conversationParticipants
                  .filter(item => !item.accessType || item.accessType === 'PARTICIPANT')
                  .filter(item => !this.state.removedRecipients.some(removed => String(removed.userId) === String(item.userId) && String(removed.permissionId || '') === String(item.permissionId || '') && removed.role === item.role))
                  .slice(0, this.state.participantsExpanded ? undefined : 3)
                  .map(item => (
                    <span key={item.userId} className={String(item.userId) === String(this.props.user.userId) ? 'is-current-user' : ''}>
                      <i className={`eventComms__participantAvatar ${item.avatar ? 'has-image' : ''}`}>
                        {item.avatar ? (
                          <img src={item.avatar} alt="" />
                        ) : item.role === 'EVENT_STAFF' ? (
                          'ES'
                        ) : (
                          item.label
                            .split(/\s+/)
                            .slice(0, 2)
                            .map(part => part.charAt(0))
                            .join('')
                        )}
                      </i>
                      <b>{item.label}</b>
                      <small>{item.roleLabel}{String(item.userId) === String(this.props.user.userId) && <em>You</em>}</small>
                      {config.canModerate && <button type="button" className="eventComms__participantRemove" aria-label={`Remove ${item.label} from this conversation`} title="Remove participant" onClick={() => this.setState(state => ({removedRecipients: [...state.removedRecipients, item]}))}>×</button>}
                    </span>
                  ))}
                {this.state.conversationParticipants.filter(item => !item.accessType || item.accessType === 'PARTICIPANT').length > 3 && (
                  <button
                    type="button"
                    className="eventComms__participantMore"
                    aria-expanded={this.state.participantsExpanded}
                    onClick={() => this.setState(state => ({ participantsExpanded: !state.participantsExpanded }))}
                  >
                    {this.state.participantsExpanded
                      ? 'Show less'
                      : `+${this.state.conversationParticipants.filter(item => !item.accessType || item.accessType === 'PARTICIPANT').length - 3}`}
                  </button>
                )}
              </div>
              {this.state.conversationParticipants.some(
                item => item.accessType === 'SAFEGUARDING' || item.accessType === 'GROUP'
              ) && (
                <>
                  <h4>Staff with safeguarding access</h4>
                  <p className="eventComms__accessNote">
                    These staff can review the conversation even when they are not direct participants.
                  </p>
                  <div className="eventComms__participantChips is-safeguarding">
                    {this.state.conversationParticipants
                      .filter(item => item.accessType === 'SAFEGUARDING' || item.accessType === 'GROUP')
                      .map(item => (
                        <span key={`${item.userId}:${item.permissionId}:${item.role}`}>
                          <i className={`eventComms__participantAvatar ${item.avatar ? 'has-image' : ''}`}>
                            {item.avatar ? (
                              <img src={item.avatar} alt="" />
                            ) : item.role === 'EVENT_STAFF' ? (
                              'ES'
                            ) : (
                              item.label
                                .split(/\s+/)
                                .slice(0, 2)
                                .map(part => part.charAt(0))
                                .join('')
                            )}
                          </i>
                          <b>{item.label}</b>
                          <small>{item.roleLabel}</small>
                        </span>
                      ))}
                  </div>
                </>
              )}
              {config.canModerate && (
                <>
                  <h4>Add people</h4>
                  <div className="eventComms__recipientFields">
                    <div>
                      <input
                        type="search"
                        value={this.state.recipientSearch}
                        onChange={event => this.setState({ recipientSearch: event.target.value })}
                        placeholder="Search people…"
                      />
                      <span className="eventComms__select"><select value={this.state.recipientRole} onChange={event => this.setState({ recipientRole: event.target.value })}>
                        <option value="ALL">All roles</option>
                        <option value="PARENT">Parents</option>
                        <option value="STUDENT">Students</option>
                        <option value="ADMIN">Admins</option>
                        <option value="MANAGER">Managers</option>
                        <option value="TEACHER">Teachers</option>
                        <option value="COACH">Coaches</option>
                      </select></span>
                    </div>
                    <button type="button" onClick={this.selectAllFiltered}>
                      Select all filtered
                    </button>
                  </div>
                  <div className="eventComms__participantAdd">
                    {this.filteredRecipients().map(item => (
                      <label key={`${item.userId}:${item.role}`}>
                        <input
                          type="checkbox"
                          checked={this.state.selectedRecipients.some(
                            value => value.userId === item.userId && value.permissionId === item.permissionId && value.role === item.role
                          )}
                          onChange={() => this.toggleRecipient(item)}
                        />
                        <span>
                          {item.label}
                          <small>{item.roleLabel}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              </div>
              <footer>
                <button
                  type="button"
                  className="is-secondary"
                  onClick={() => this.setState({ participantsOpen: false })}
                >
                  Close
                </button>
                {selected && selected.type === 'PRIVATE' && (config.canModerate || String(selected.createdByUserId) === String(this.props.user.userId) && !selected.staffRenamedTitle) && (
                  <button type="button" className="is-primary" onClick={this.saveConversationDetails}>
                    Save changes
                  </button>
                )}
              </footer>
          </AccessibleModal>
        )}
      </section>
    );
  }
}
